import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import UploadCard from "../../components/commons/UploadCard";
import VideoCarousel from "../../components/partials/video/VideoCarousel";
import VideoTextSection from "../../components/partials/video/VideoTextFields";
import Button from "../../components/commons/Button";
import type { Video } from "../../services/webServices/video/types";
import { getVideos } from "../../services/webServices/video/get";
import { postVideo } from "../../services/webServices/video/post";
import { patchVideo } from "../../services/webServices/video/patch";
import { deleteVideo } from "../../services/webServices/video/delete";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

export default function Video() {

  const [videos, setVideos] = useState<Video[]>([]);
  const [editingVideo, setEditingVideo] =  useState<Video | null>(null);

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [coverFile, setCoverFile] = useState<File | null>(null);

  const [description, setDescription] = useState("");

  const [formKey, setFormKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchVideos = async () => {

    if (isFirstLoad) showLoading();
    setIsLoading(true);

    const data = await getVideos();
    setVideos(data);

    setIsLoading(false);
    if (isFirstLoad) hideLoading();
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const handleEdit = (video: Video) => {
    setEditingVideo(video);
    setDescription(video.description);
    setVideoFile(null);
    setCoverFile(null);
    setFormKey(prev => prev + 1);
  };

  const handleDelete = async (uuid: string) => {
    const success = await deleteVideo({ uuid });

    if (!success) return false;

    toast.success("ویدیو با موفقیت حذف شد");
    if (editingVideo?.uuid === uuid) resetForm();
    fetchVideos();
  };

  const resetForm = () => {
    setEditingVideo(null);
    setVideoFile(null);
    setCoverFile(null);
    setDescription("");
    setFormKey(prev => prev + 1);
  };

  const handleSubmit = async () => {
    if (
      !editingVideo &&
      (!videoFile || !coverFile || !description)
    ) {
      toast.info("لطفا همه فیلدها را کامل کنید");
      return;
    }

    setIsLoading(true);

    if (!editingVideo) {
      const success = await postVideo({
        videoFile: videoFile!,
        coverFile: coverFile!,
        description: description,
      });

      if (!success) return;

      toast.success("ویدیو با موفقیت ثبت شد");
    } else {
      const success = await patchVideo({
        uuid: editingVideo.uuid,
        originalVideo: editingVideo,
        videoFile: videoFile,
        coverFile: coverFile,
        description: description,
      });

      if (!success) return;

      toast.success("ویدیو با موفقیت ویرایش شد");
    }

    resetForm();
    fetchVideos();
    setIsLoading(false);
  };

  return (
    <div className="flex flex-col gap-4">
      <VideoCarousel
        video={videos}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <div className="flex flex-col gap-4">
        <div className="flex gap-4">
          <UploadCard
            key={`video-content-${formKey}`}
            title="ویدیو"
            description="این فایل به‌عنوان ویدیو در صفحه اصلی نمایش داده می‌شود."
            helperText="تنها فرمت‌های MP4 و WebM پشتیبانی می‌شوند"
            mode="video"
            cropAspect={16 / 9}
            initialValue={
              editingVideo
                ? {
                    type: "video",
                    previewUrl:
                      process.env.REACT_APP_File_URL + editingVideo.content,
                  }
                : undefined
            }
            onChange={setVideoFile}

          />

          <UploadCard
            key={`video-cover-${formKey}`}
            title="تصویر کاور"
            description="این تصویر به‌عنوان کاور ویدیو نمایش داده می‌شود."
            helperText="تنها فرمت‌های JPG و PNG پشتیبانی می‌شوند"
            mode="image"
            cropAspect={16 / 9}
            initialValue={
              editingVideo
                ? {
                    type: "image",
                    previewUrl:
                      process.env.REACT_APP_File_URL +
                      editingVideo.cover_image,
                  }
                : undefined
            }
            onChange={setCoverFile}
          />
        </div>

        <VideoTextSection
          description={description}
          onDescriptionChange={setDescription}
        />

        <div className="flex justify-end gap-4">
          <Button
            variant="gray"
            style="solid"
            size="lg"
            text="انصراف"
            loading={isLoading}
            disabled={false}
            onClick={resetForm}
          />

          <Button
            variant="primary"
            style="solid"
            size="lg"
            text={editingVideo ? "ویرایش" : "ارسال"}
            loading={isLoading}
            disabled={false}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};